import React from 'react';
import { styled } from '@mui/material/styles';
import { Button, Stack, useMediaQuery, Box } from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { GrayIconButton } from 'components/Button';

/************************* Styled Components  **************************/
const Container = styled(Stack)(({ open = 0, position, width, left, theme }) => ({
  position: position ?? 'fixed',
  bottom: 0,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: 42,
  width:
    width ??
    (open === 2 ? 'calc(100% - 390px)' : open === 1 ? 'calc(100% - 122px)' : 'calc(100% - 50px)'),
  padding: '8px',
  left: left ?? (open === 2 ? '365px' : open === 1 ? '97px' : '25px'),
  right: '25px',
  zIndex: 1200,
  backgroundColor: theme.palette.background.secondary,
  border: `1px solid ${theme.palette.border.separator}`,
  borderRadius: '0 0 10px 10px',

  '@media (max-width: 1280px) and (min-width: 906px)': {
    left: left ?? (open === 2 ? '372px' : open === 1 ? '104px' : '32px'),
    right: '32px',
    width:
      width ??
      (open === 2 ? 'calc(100% - 404px)' : open === 1 ? 'calc(100% - 136px)' : 'calc(100% - 64px)'),
  },
  '@media (max-width: 904px)': {
    left: '0px',
    right: '0px',
    width: '100%',
    height: 'auto',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: '6px 12px',
  },

  '@media(min-width: 601px)': {
    padding: '8px 24px',
    border: `1px solid ${theme.palette.border.separator}`,
  },

  fontFeatureSettings: `'cv11' on, 'cv09' on, 'cv03' on, 'cv04' on, 'cv02' on, 'ss01' on`,
}));

const PageButton = styled(Button)(({ theme, active }) => ({
  minWidth: 26,
  height: 26,
  borderRadius: 6,
  color: active ? theme.palette.background.secondary : theme.palette.text.tertiary,
  backgroundColor: active ? theme.palette.text.tertiary : 'transparent',
  border: 'none',
  fontSize: 11,
  fontWeight: 500,

  '&:hover': {
    backgroundColor: active ? theme.palette.text.tertiary : theme.palette.background.hover,
  },

  '&:disabled': {
    opacity: 0.24,
    cursor: 'not-allowed',
  },

  '&.ellipsis': {
    color: '#ffffff !important',
    cursor: 'default',

    '& *': {
      color: '#ffffff !important',
    },

    '&:hover': {
      backgroundColor: 'transparent',
      color: '#ffffff !important',
    },

    '&:disabled': {
      color: '#ffffff !important',
      opacity: 1,
    },
  },

  '&.arrow-button': {
    color: theme.palette.text.tertiary,
    backgroundColor: 'transparent',

    '&:hover': {
      backgroundColor: theme.palette.background.hover,
    },

    '&:disabled': {
      opacity: 0.3,
      backgroundColor: 'transparent',
    },
  },
}));

const CurrentShower = styled('div')(({ theme }) => ({
  color: theme.palette.text.main,
  fontSize: 11,
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  flex: '1 1 0',
  justifyContent: 'flex-start',
  lineHeight: 1.2,
  minHeight: 26,

  '@media (max-width: 904px)': {
    display: 'none',
  },
}));

const PageNavigation = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.75),
  flexWrap: 'wrap',
  flex: '1 1 0',

  '@media (max-width: 904px)': {
    flex: 'none',
    order: 2,
    gap: theme.spacing(0.5),
  },
}));

const ScrollButtonContainer = styled(Box)(() => ({
  flex: '1 1 0',
  display: 'flex',
  justifyContent: 'flex-end',

  '@media (max-width: 904px)': {
    display: 'none',
  },
}));

/************************* Main Component  **************************/
const Pagination = ({
  counts,
  currentPage,
  perPage,
  next,
  previous,
  open,
  position,
  width,
  left,
  scrollRef,
  onPageChange,
}) => {
  const below600 = useMediaQuery('(max-width: 600px)');

  const totalPages = Math.ceil(counts / perPage);

  const calculateItemRange = (page) => {
    const from = perPage * (page - 1);
    let to = from + perPage;
    if (to > counts) {
      to = counts;
    }
    return `${from + 1} to ${to}`;
  };

  const getPageNumbers = () => {
    const maxVisible = below600 ? 3 : 5;
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const sidePages = Math.floor((maxVisible - 3) / 2);
    const start = Math.max(2, Math.min(currentPage - sidePages, totalPages - maxVisible + 2));
    const end = Math.min(totalPages - 1, start + maxVisible - 3);

    return [
      1,
      ...(start > 2 ? ['...'] : []),
      ...Array.from({ length: end - start + 1 }, (_, i) => start + i),
      ...(end < totalPages - 1 ? ['...'] : []),
      ...(totalPages > 1 ? [totalPages] : []),
    ];
  };

  const handlePageClick = (page) => {
    if (typeof page === 'number' && page !== currentPage) {
      if (onPageChange) {
        onPageChange(page);
      }
    }
  };

  return totalPages <= 1 ? null : (
    <Container
      open={open}
      position={position}
      width={width}
      left={left}
      className="MuiTablePagination-root"
    >
      <CurrentShower>
        Showing {calculateItemRange(currentPage)} of {counts} results
      </CurrentShower>
      <PageNavigation>
        <PageButton
          className="arrow-button"
          onClick={previous}
          disabled={currentPage === 1}
          sx={{ minWidth: 32 }}
        >
          <ChevronLeftIcon />
        </PageButton>

        {getPageNumbers().map((page, index) => (
          <PageButton
            key={index}
            active={page === currentPage}
            onClick={() => handlePageClick(page)}
            disabled={page === '...'}
            className={page === '...' ? 'ellipsis' : ''}
          >
            {page}
          </PageButton>
        ))}

        <PageButton
          className="arrow-button"
          onClick={next}
          disabled={currentPage >= totalPages}
          sx={{ minWidth: 32 }}
        >
          <ChevronRightIcon />
        </PageButton>
      </PageNavigation>

      <ScrollButtonContainer>
        <GrayIconButton
          sx={{ width: 32, height: 32 }}
          onClick={() => {
            if (scrollRef?.current) {
              scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
        >
          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
        </GrayIconButton>
      </ScrollButtonContainer>
    </Container>
  );
};

export default Pagination;
